// Utility functions for handling images from S3

const S3_BUCKET = "autochef-assets";
const S3_REGION = "us-east-2";
const S3_BASE_URL = `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com`;

/**
 * Convert a relative image path to an S3 URL
 * @param {string} path - The relative path (e.g., "images/recipeImages/12.jpg" or "/images/recipeImages/12.jpg")
 * @returns {string} - Full S3 URL
 * 
 * Example:
 *   getS3ImageUrl("images/recipeImages/12.jpg")
 *   // Returns: "https://autochef-assets.s3.us-east-2.amazonaws.com/images/recipeImages/12.jpg"
 */
export const getS3ImageUrl = (path) => {
  if (!path) return null;
  
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  return `${S3_BASE_URL}/${cleanPath}`;
};

/**
 * Get recipe image URL from recipe ID
 * @param {number|string} recipeId - The recipe ID
 * @returns {string} - Full S3 URL for the recipe image
 */
export const getRecipeImageUrl = (recipeId) => {
  return getS3ImageUrl(`images/recipeImages/${recipeId}.jpg`);
};

/**
 * Get ingredient image URL from ingredient ID
 * @param {number|string} ingredientId - The ingredient ID
 * @returns {string} - Full S3 URL for the ingredient image
 */
export const getIngredientImageUrl = (ingredientId) => {
  return getS3ImageUrl(`images/ingredientImages/${ingredientId}.jpg`);
};

/**
 * Get cuisine image URL from cuisine name
 * @param {string} cuisineName - The cuisine name (e.g., "italian", "chinese")
 * @returns {string} - Full S3 URL for the cuisine image
 */
export const getCuisineImageUrl = (cuisineName) => {
  return getS3ImageUrl(`images/cuisineImages/${cuisineName.toLowerCase()}.jpg`);
};

/**
 * Get diet image URL from diet name
 * @param {string} dietName - The diet name (e.g., "Keto", "Vegan")
 * @returns {string} - Full S3 URL for the diet image
 */
export const getDietImageUrl = (dietName) => {
  return getS3ImageUrl(`images/dietImages/${dietName}.jpg`);
};

/**
 * Get tag image URL from tag name
 * @param {string} tagName - The tag name
 * @returns {string} - Full S3 URL for the tag image
 */
export const getTagImageUrl = (tagName) => {
  return getS3ImageUrl(`images/tagImages/${tagName}.jpg`);
};

/**
 * Get icon URL from icon path
 * @param {string} iconPath - The icon path (e.g., "cuisineOptions/Italian.svg")
 * @returns {string} - Full S3 URL for the icon
 */
export const getIconUrl = (iconPath) => {
  return getS3ImageUrl(`icons/${iconPath}`);
};

